import { TaJson } from "ta-json";

import Guard from "../guard";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";
import { ResponseMessage } from "./response-message";

export interface ICommandsClient {
    /**
     * Executes a Content Hub command.
     * @param namespace - The name of the folder containing the command
     * @param command - The name of the command
     * @param args - The arguments of the command or null/undefined if the command has no arguments
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The raw HTTP response.
     */
    executeCommandRawAsync(
        namespace: string,
        command: string,
        args?: unknown,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<unknown>>;

    /**
     * Executes a Content Hub command.
     * @param namespace - The name of the folder containing the command
     * @param command - The name of the command
     * @param args - The arguments of the command or null/undefined if the command has no arguments
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns The result of the command. If the command has no result, null is returned.
     */
    executeCommandAsync(
        namespace: string,
        command: string,
        args?: unknown,
        cancelCallback?: CancelCallback
    ): Promise<unknown>;
}

export class CommandsClient implements ICommandsClient {
    private readonly _client: IExtendedContentHubClient;
    constructor(client: IExtendedContentHubClient) {
        Guard.notNull(client);

        this._client = client;
    }

    async executeCommandRawAsync(
        namespace: string,
        command: string,
        args: unknown = null,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage<unknown>> {
        Guard.stringNotNullOrEmpty(namespace);
        Guard.stringNotNullOrEmpty(command);

        const link = await this._client.linkHelper.commandToLinkAsync(namespace, command);

        const content = TaJson.serialize(args ?? {}) as unknown;
        const response = await this._client.raw.postAsync(link.href, content, undefined, cancelCallback);

        ResponseHandler.handleErrors(response);

        return response;
    }

    async executeCommandAsync(
        namespace: string,
        command: string,
        args: unknown = null,
        cancelCallback?: CancelCallback
    ): Promise<unknown> {
        Guard.stringNotNullOrEmpty(namespace);
        Guard.stringNotNullOrEmpty(command);

        const response = await this.executeCommandRawAsync(namespace, command, args, cancelCallback);

        let result = null;
        if (response.content != null) {
            result = response.content;
        }

        return result;
    }
}
